/** Numbers typed by admins by hand (review INT-2 / INT-7).
 *
 *  Russian input groups digits with spaces («1 000», often a no-break space after a paste)
 *  and writes decimals with a comma («12,5»). `Number()` turns both into NaN, and
 *  `JSON.stringify` sends NaN as `null` — for a promo code limit the API reads `null` as
 *  «unlimited». So: parse here, show an error for text that is not a number, never send NaN.
 *  Money stays a decimal string for the server's Decimal; nothing here becomes a float. */

const WHOLE = /^\d{1,9}$/ // ≤ 999 999 999 fits the INTEGER columns
const DECIMAL = /^\d+(\.\d+)?$/

/** Whole count: '' → null («not set»), '1 000' → 1000, anything else → undefined (invalid). */
export function parseCount(raw: string): number | null | undefined {
  const s = raw.replace(/\s/g, '')
  if (s === '') return null
  return WHOLE.test(s) ? Number(s) : undefined
}

/** Decimal amount as the API takes it: '12,5' → '12.5', ' 1 000 ' → '1000', '' → null,
 *  anything else (a sign, two commas, letters) → undefined (invalid). */
export function parseDecimal(raw: string): string | null | undefined {
  const s = raw.replace(/\s/g, '').replace(',', '.')
  if (s === '') return null
  return DECIMAL.test(s) ? s : undefined
}

/** Compare two non-negative decimal strings (as `parseDecimal` returns them, or as the API
 *  sends them) without floats: -1, 0 or 1. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const [ai = '', af = ''] = canonDecimal(a).split('.')
  const [bi = '', bf = ''] = canonDecimal(b).split('.')
  if (ai.length !== bi.length) return ai.length < bi.length ? -1 : 1
  if (ai !== bi) return ai < bi ? -1 : 1
  const n = Math.max(af.length, bf.length)
  const x = af.padEnd(n, '0')
  const y = bf.padEnd(n, '0')
  return x === y ? 0 : x < y ? -1 : 1
}

/** One spelling per decimal value, for comparisons only: '30.0000' → '30', '012.50' → '12.5'. */
export function canonDecimal(s: string): string {
  const m = /^(\d+)(?:\.(\d*))?$/.exec(s)
  if (!m) return s
  const whole = (m[1] ?? '').replace(/^0+(?=\d)/, '')
  const frac = (m[2] ?? '').replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole
}

// ── exact sums of API decimals (totals rows) ──

const SIGNED = /^(-?)(\d+)(?:\.(\d+))?$/

function scaled(s: string, scale: number): bigint | null {
  const m = SIGNED.exec(s.trim())
  if (!m) return null
  const frac = (m[3] ?? '').padEnd(scale, '0').slice(0, scale)
  const v = BigInt(`${m[2] ?? '0'}${frac}`)
  return m[1] === '-' ? -v : v
}

function unscaled(v: bigint, scale: number): string {
  const neg = v < 0n
  const digits = (neg ? -v : v).toString().padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const frac = digits.slice(digits.length - scale)
  return `${neg ? '-' : ''}${whole}${scale > 0 ? `.${frac}` : ''}`
}

/** Sum of decimal strings as the API sends them ("12.50", "-3.1"), exact (BigInt, no float);
 *  the result keeps `scale` decimals (2 = kopecks). Text that is not a number counts as 0. */
export function sumDecimal(values: readonly string[], scale = 2): string {
  let total = 0n
  for (const v of values) total += scaled(v, scale) ?? 0n
  return unscaled(total, scale)
}

/** `a − b` of two decimal strings, exact. */
export function subDecimal(a: string, b: string, scale = 2): string {
  return unscaled((scaled(a, scale) ?? 0n) - (scaled(b, scale) ?? 0n), scale)
}

/** a × b rounded half up to `scale` decimals (2 = kopecks), exact — the preview of what the
 *  server stores as q(amount × rate). Non-numbers count as 0. */
export function mulDecimal(a: string, b: string, scale = 2): string {
  const inScale = 9 // enough for NUMERIC(18,6) amounts and NUMERIC(14,6) rates
  const x = scaled(a, inScale) ?? 0n
  const y = scaled(b, inScale) ?? 0n
  const product = x * y // scale 2·inScale
  const div = 10n ** BigInt(2 * inScale - scale)
  const neg = product < 0n
  const abs = neg ? -product : product
  const rounded = (abs * 2n + div) / (2n * div) // half up, away from zero
  return unscaled(neg ? -rounded : rounded, scale)
}

/** part / whole × 100 with two decimals, half up ("0.00" when whole is 0) — exact. */
export function pctOf(part: string, whole: string): string {
  const p = scaled(part, 6) ?? 0n
  const w = scaled(whole, 6) ?? 0n
  if (w === 0n) return '0.00'
  const neg = p < 0n !== w < 0n && p !== 0n
  const ap = p < 0n ? -p : p
  const aw = w < 0n ? -w : w
  const hundredths = (ap * 10000n * 2n + aw) / (2n * aw) // round half up
  return unscaled(neg ? -hundredths : hundredths, 2)
}
